<template>
  <div id="mobility-text" :class="classes">
    <!-- <transition name="fade"> -->
      <span v-if="$store.state.showDetailedInfo">
        <p v-for="copy in copyData[year].copy" :key="copy" v-html="copy"></p>
      </span>
    <!-- </transition> -->
  </div>
</template>
<script>
import progressionMixin from "@/mixins/progressionMixin";

export default {
  name: "MobilityText",
  mixins: [progressionMixin],
  computed: {
    year() {
      return this.$store.state.year;
    }
  },
  data() {
    return {
      copyData: {
        0: {
          copy: [
            "The Expanded Disability Status Scale, or EDSS, is a method of quantifying physical disability in MS and monitoring changes in the level of disability over time.<sup>4</sup>",
            "Based on neurological examinations assessing a range of neurological functions, results are presented on a scale from zero to ten, with zero indicating normal neurologic function, seven being confined to a wheelchair, and progressing to death at ten.<sup>4,5</sup>"
          ]
        },
        1: {
          copy: [
            "The EDSS score is 2, representing minimal disability.<sup>4</sup>",
            "Here we focus on the whole body, to illustrate mobility issues and changes in physical disability."
          ]
        },
        2: {
          copy: [
            "During a relapse the EDSS jumps to a score of 5.5, which represents increased limitations in walking ability.<sup>4</sup>",
            "Gait dysfunction is a common symptom in MS, reported by almost 90% of patients after 20 years of disease progression.<sup>12,13</sup>"
          ]
        },
        3: {
          copy: [
            "Following a relapse, the EDSS score returns to a minimal disability level of 2.5.<sup>4</sup>",
            "Increasing gait impairment is a significant problem in MS, leading to decreased activity and limitations in function.<sup>17</sup>"
          ]
        },
        4: {
          copy: [
            "The patient is possibly experiencing another relapse, as indicated by an EDSS score of 6.5, which requires the use of a cane to walk.<sup>4</sup>",
            "Most relapses are monofocal (71.4%), and the most common relapse phenotype is sensory (48%), followed by weakness (34%), and problems with visual acuity (20%).<sup>21</sup>"
          ]
        },
        5: {
          copy: [
            "Following the relapse, the patient has not fully recovered, and the EDSS score is 3.5, which indicates the patient is still fully ambulatory.<sup>4</sup>",
            "Sensory impairments may include numbness, neuropathic pain, temperature changes, and other abnormal sensations most commonly reported in the limbs and extremities.<sup>25,26</sup>"
          ]
        },
        6: {
          copy: [
            "The patient’s EDSS score is 3.5, which indicates the patient is still fully ambulatory.<sup>4</sup>",
            "As the disease progresses, patients are likely to experience increasing gait variability, and walking speed is also impacted.<sup>12</sup>"
          ]
        },
        7: {
          copy: [
            "The patient is experiencing another relapse, as indicated by an EDSS score of 6, which requires the use of a cane to walk.<sup>4</sup>",
            "Women present more often with sensory or visual symptoms, while men are more prone to pyramidal, brainstem, and cerebellar relapses.<sup>21</sup>"
          ]
        },
        8: {
          copy: [
            "Following the relapse, the EDSS score lowers to 5, which represents moderate disability.<sup>4</sup>",
            "This level of disability is severe enough to impair full daily activities. For example, an individual may not be able to work a full day without special provisions.<sup>4</sup>"
          ]
        },
        9: {
          copy: [
            "The EDSS score remains at 5.<sup>4</sup>",
            "Gait and balance problems are some of the symptoms most strongly associated with reduced quality of life, as measured on the EQ-5D scale.<sup>38</sup>"
          ]
        },
        10: {
          copy: [
            "The EDSS score remains at 5.<sup>4</sup>",
            "Fatigue, depression, and spasticity also contribute to the disease burden of RRMS.<sup>38</sup>"
          ]
        },
        11: {
          copy: [
            "The patient is experiencing another relapse, as indicated by an EDSS score of 6.5, which requires the use of a cane to walk.<sup>4</sup>",
            "Relapses are fewer in SPMS and patients have incomplete recovery during this stage of the disease course.<sup>2</sup>"
          ]
        },
        12: {
          copy: [
            "After the relapse, the patient’s EDSS score is 5.5, indicating moderate disability.<sup>4</sup>",
            "Gait disturbance in SPMS is characterized by asymmetry, ataxia, muscular weakness, and impaired dynamic stability.<sup>12</sup>"
          ]
        },
        13: {
          copy: [
            "The disability level of the patient has continued to increase to an EDSS score of 6.<sup>4</sup>",
            "At this disability level, intermittent or unilateral constant assistance, with a cane, crutch, or brace, is required to walk about 100 meters.<sup>4</sup>"
          ]
        },
        14: {
          copy: [
            "The disability level of the patient remains stable at an EDSS score of 6.<sup>4</sup>",
            "The step length is decreased at higher EDSS levels of 6.0 and 6.5, while the step time is significantly longer.<sup>46</sup>"
          ]
        },
        15: {
          copy: [
            "The disability level of the patient has increased slightly to an EDSS score of 6.5.<sup>4</sup>",
            "There is an increase in the percentage of double support time (period of time when both feet are in contact with the ground) with increasing EDSS levels from 3.0 to 6.5.<sup>46</sup>"
          ]
        },
        16: {
          copy: [
            "The disability level of the patient remains stable at an EDSS score of 6.5.<sup>5</sup>",
            "While gait and balance problems are the most important symptoms affecting quality of life in patients with RRMS, spasticity, paralysis, and bowel problems are important for patients with SPMS.<sup>38</sup>"
          ]
        },
        17: {
          copy: [
            "The disability level of the patient remains stable at an EDSS score of 6.5.",
            "The decrease in step length is observed up to EDSS level 6, which plateaus through to 6.5.<sup>46</sup>"
          ]
        },
        18: {
          copy: [
            "The disability level of the patient has progressed to an EDSS of 7, which indicates the patient is unable to walk beyond 5 meters even with aid and may need a wheelchair.<sup>4</sup>",
            "One of the strongest predictors of MS evolution is age at MS onset, with shorter progression times for older patients.<sup>41</sup>"
          ]
        },
        19: {
          copy: [
            "The disability level of the patient has increased to an EDSS score of 7.5.<sup>4</sup>",
            "Patients with SPMS may eventually decline to a state of complete disability,<sup>52</sup> requiring palliative care. This disease progression is associated with decreases in physical functioning and health-related quality of life."
          ]
        },
        20: {
          copy: [
            "The EDSS score is 8, indicating the need for a wheelchair.<sup>4</sup>",
            "Within 20 years of disease onset, approximately 17% of patients will experience disease progression requiring the use of a wheelchair.<sup>13</sup>"
          ]
        }
      }
    };
  }
};
</script>

<style lang="scss">
#mobility-text {
  position: absolute;
  top: 50px;
  right: 10px;

  &.subtitle {
    position: relative;
    top: 0;
    right: auto;
    width: 100%;
  }
}

p {
  font-size: rem(16px);
  margin-bottom: rem(20px);
}
</style>